import { useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { User, UserRole } from "../types";
import { profileToUser } from "../types";
import type { ProfileRow } from "../types";
import { uuid } from "../utils/uuid";
import { BUDGET_TYPE_CATALOG_NAMES } from "../utils/budgetCatalog";

export function useAuth() {
  const loginAdmin = useCallback(
    async (
      email: string,
      password: string
    ): Promise<{ user: User | null; error: string | null; needsMfa?: boolean }> => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) return { user: null, error: error.message };
        const uid = data.user?.id;
        if (!uid) return { user: null, error: "Session invalide" };
        const aal = (data.session as { user?: { aal?: string } } | undefined)?.user?.aal;
        if (aal === "aal1") {
          const { data: factorsData } = await supabase.auth.mfa.listFactors();
          const totpFactors =
            factorsData?.totp ?? (factorsData as { totp?: unknown[] } | undefined)?.totp ?? [];
          if (totpFactors.length > 0) {
            return { user: null, error: null, needsMfa: true };
          }
        }
        const { data: prof, error: pe } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .maybeSingle();
        if (pe) return { user: null, error: pe.message };
        if (!prof) return { user: null, error: "Profil admin introuvable" };
        return { user: profileToUser(prof as ProfileRow), error: null };
      } catch (e) {
        return {
          user: null,
          error: e instanceof Error ? e.message : "Erreur connexion admin",
        };
      }
    },
    []
  );

  const loginWithCode = useCallback(
    async (
      role: "partner" | "installer" | "commercial",
      code: string
    ): Promise<{ user: User | null; error: string | null }> => {
      try {
        const c = code.trim().toUpperCase();
        if (!c) return { user: null, error: "Code requis" };
        
        // MULTI-TENANT: Le code peut contenir un préfixe (ex: FE27-123)
        // On cherche directement avec le code entré
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("role", role)
          .eq("code", c)
          .eq("active", true)
          .maybeSingle();
        if (error) return { user: null, error: error.message };
        if (!data) return { user: null, error: "Code d'accès invalide ou inactif" };
        return { user: profileToUser(data as ProfileRow), error: null };
      } catch (e) {
        return {
          user: null,
          error: e instanceof Error ? e.message : "Erreur vérification code",
        };
      }
    },
    []
  );

  const registerAdmin = useCallback(
    async (
      name: string,
      email: string,
      password: string
    ): Promise<{ user: User | null; error: string | null }> => {
      try {
        // emailRedirectTo : après validation du lien reçu par email, l'utilisateur revient sur l'app.
        // Activer "Confirm email" dans Supabase (Authentication → Providers → Email) pour la mise en production.
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo:
              typeof window !== "undefined" ? `${window.location.origin}/` : undefined,
          },
        });
        if (error) return { user: null, error: error.message };
        const uid = data.user?.id;
        if (!uid) return { user: null, error: "Inscription échouée" };
        
        // Générer un code unique pour chaque admin (ex: ADMIN-A1B2C3)
        const uniqueCode = `ADMIN-${uid.substring(0, 6).toUpperCase()}`;
        
        const profile = {
          id: uid,
          name,
          role: "admin" as UserRole,
          code: uniqueCode,
          email,
          active: true,
          is_owner: true,
        };
        const { error: ins } = await supabase.from("profiles").insert(profile);
        if (ins) return { user: null, error: ins.message };
        
        // Région de stock par défaut + stock initial (si table stock_regions existe)
        let defaultRegionId: string | null = null;
        const defaultRegionIdOrUuid = uuid();
        const { error: regionErr } = await supabase.from("stock_regions").insert({
          id: defaultRegionIdOrUuid,
          owner_id: uid,
          name: "Principal",
        });
        if (!regionErr) defaultRegionId = defaultRegionIdOrUuid;
        const defaultStock = [
          { id: `${uid}-leds`, name: "Dalles / Spots", quantity: 150, owner_id: uid, ...(defaultRegionId && { region_id: defaultRegionId }) },
          { id: `${uid}-drivers`, name: "Alimentations", quantity: 50, owner_id: uid, ...(defaultRegionId && { region_id: defaultRegionId }) },
          { id: `${uid}-cables`, name: "Câblage (m)", quantity: 500, owner_id: uid, ...(defaultRegionId && { region_id: defaultRegionId }) },
          ...BUDGET_TYPE_CATALOG_NAMES.map((name, index) => ({
            id: `${uid}-type-${index}`,
            name,
            quantity: 0,
            owner_id: uid,
            low_stock_threshold: 0,
            ...(defaultRegionId && { region_id: defaultRegionId }),
          })),
        ];
        await supabase.from("inventory").insert(defaultStock);

        // Abonnement : période d'essai 7 jours pour le nouvel owner
        const trialEndsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
        const { error: subErr } = await supabase.from("subscriptions").insert({
          owner_id: uid,
          trial_ends_at: trialEndsAt,
          status: "trialing",
        });
        if (subErr) return { user: null, error: subErr.message };
        
        return { user: profileToUser(profile as ProfileRow), error: null };
      } catch (e) {
        return {
          user: null,
          error: e instanceof Error ? e.message : "Erreur inscription admin",
        };
      }
    },
    []
  );

  const requestVerificationCode = useCallback(
    async (
      role: "partner" | "installer" | "commercial",
      email: string
    ): Promise<{ success: boolean; error: string | null; expiresAt?: string; emailSent?: boolean }> => {
      try {
        const e = email.trim();
        if (!e) return { success: false, error: "Email requis" };
        
        // Vérifier que le compte existe
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("name")
          .eq("email", e)
          .eq("role", role)
          .eq("active", true)
          .single();
        
        if (profileError || !profileData) {
          return { success: false, error: "Aucun compte trouvé avec cet email" };
        }
        
        // Générer le code
        const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
        const expiresAt = new Date(Date.now() + 10 * 60 * 1000);
        
        // Invalider les anciens codes
        await supabase
          .from("verification_codes")
          .update({ verified: true })
          .eq("email", e)
          .eq("verified", false);
        
        // Insérer le nouveau code
        const { error: insertError } = await supabase
          .from("verification_codes")
          .insert({
            email: e,
            role: role,
            verification_code: verificationCode,
            expires_at: expiresAt.toISOString(),
            verified: false,
            attempts: 0,
          });
        
        if (insertError) {
          console.error("Erreur insertion:", insertError);
          return { success: false, error: "Erreur création code" };
        }

        // En production : envoi du code par email via Edge Function + SMTP Supabase (Resend).
        // En dev, le code est en base ; pour envoyer l'email, implémenter une Edge Function avec Resend.
        return {
          success: true,
          error: null,
          expiresAt: expiresAt.toISOString(),
          emailSent: false,
        };
      } catch (e) {
        console.error("Erreur:", e);
        return {
          success: false,
          error: e instanceof Error ? e.message : "Erreur envoi code",
        };
      }
    },
    []
  );

  const verifyCodeAndRecover = useCallback(
    async (
      role: "partner" | "installer" | "commercial",
      email: string,
      verificationCode: string
    ): Promise<{ code: string | null; name: string | null; error: string | null }> => {
      try {
        const e = email.trim();
        const vc = verificationCode.trim();
        
        if (!e) return { code: null, name: null, error: "Email requis" };
        if (!vc) return { code: null, name: null, error: "Code de vérification requis" };
        
        // Chercher le code de vérification
        const { data: verificationData, error: verificationError } = await supabase
          .from("verification_codes")
          .select("id, attempts, expires_at")
          .eq("email", e)
          .eq("role", role)
          .eq("verification_code", vc)
          .eq("verified", false)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (verificationError) {
          return { code: null, name: null, error: verificationError.message };
        }
        
        if (!verificationData) {
          return { code: null, name: null, error: "Code de vérification invalide ou expiré" };
        }
        
        // Vérifier l'expiration
        if (new Date(verificationData.expires_at) < new Date()) {
          return { code: null, name: null, error: "Code de vérification expiré" };
        }
        
        // Vérifier le nombre de tentatives
        if (verificationData.attempts >= 3) {
          // Invalider le code
          await supabase
            .from("verification_codes")
            .update({ verified: true })
            .eq("id", verificationData.id);
          
          return { code: null, name: null, error: "Trop de tentatives. Veuillez demander un nouveau code" };
        }
        
        // Marquer comme vérifié (code correct) — on n'incrémente les tentatives qu'en cas d'échec
        await supabase
          .from("verification_codes")
          .update({ verified: true })
          .eq("id", verificationData.id);
        
        // Récupérer les informations du compte
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("code, name")
          .eq("email", e)
          .eq("role", role)
          .eq("active", true)
          .single();
        
        if (profileError || !profileData) {
          return { code: null, name: null, error: "Compte introuvable" };
        }
        
        return { 
          code: profileData.code, 
          name: profileData.name, 
          error: null 
        };
      } catch (e) {
        return {
          code: null,
          name: null,
          error: e instanceof Error ? e.message : "Erreur vérification code",
        };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
  }, []);

  const verifyMfaAndGetUser = useCallback(
    async (code: string): Promise<{ user: User | null; error: string | null }> => {
      try {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const totpList = (factorsData as { totp?: { id: string }[] })?.totp ?? [];
        const factorId = totpList[0]?.id;
        if (!factorId) return { user: null, error: "Facteur MFA introuvable" };
        const { data: challengeData, error: chErr } = await supabase.auth.mfa.challenge({
          factorId,
        });
        if (chErr) return { user: null, error: chErr.message };
        const challengeId = (challengeData as { id: string })?.id;
        if (!challengeId) return { user: null, error: "Échec du défi MFA" };
        const { data: verifyData, error: vErr } = await supabase.auth.mfa.verify({
          factorId,
          challengeId,
          code: code.trim(),
        });
        if (vErr) return { user: null, error: vErr.message };
        const uid = (verifyData as { session?: { user?: { id: string } } })?.session?.user?.id;
        if (!uid) return { user: null, error: "Session invalide après vérification" };
        const { data: prof, error: pe } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .maybeSingle();
        if (pe) return { user: null, error: pe.message };
        if (!prof) return { user: null, error: "Profil admin introuvable" };
        return { user: profileToUser(prof as ProfileRow), error: null };
      } catch (e) {
        return {
          user: null,
          error: e instanceof Error ? e.message : "Erreur vérification MFA",
        };
      }
    },
    []
  );

  /** Partenaire/poseur : obtient l’URL du magic link et/ou le token_hash + profil pour établir une session (connexion par code → RLS). */
  const getMagicLinkForCode = useCallback(
    async (
      role: "partner" | "installer" | "commercial",
      code: string
    ): Promise<{ url: string | null; token_hash: string | null; profile: ProfileRow | null; error: string | null }> => {
      try {
        const c = code.trim().toUpperCase();
        if (!c) return { url: null, token_hash: null, profile: null, error: "Code requis" };
        // Même format partout (avec slash à la racine) pour que la connexion poseur marche en ligne et en local.
        const redirectTo =
          typeof window !== "undefined"
            ? `${window.location.origin}${window.location.pathname === "/" ? "/" : window.location.pathname}`
            : undefined;
        const { data, error } = await supabase.functions.invoke("login-by-code", {
          body: { code: c, role, redirect_to: redirectTo },
        });
        if (error) return { url: null, token_hash: null, profile: null, error: error.message };
        const err = (data as { error?: string })?.error;
        if (err) return { url: null, token_hash: null, profile: null, error: err };
        const payload = data as { url?: string; token_hash?: string; profile?: ProfileRow | null };
        const url = payload?.url ?? null;
        const token_hash = payload?.token_hash ?? null;
        const profile = payload?.profile ?? null;
        return { url, token_hash, profile, error: null };
      } catch (e) {
        return {
          url: null,
          token_hash: null,
          profile: null,
          error: e instanceof Error ? e.message : "Erreur connexion",
        };
      }
    },
    []
  );

  /** Établit la session partenaire/poseur via token_hash (sans redirection magic link). Si profileFromApi est fourni, on l’utilise au lieu de charger le profil (évite RLS / id ≠). */
  const verifyTokenHashAndGetUser = useCallback(
    async (
      tokenHash: string,
      profileFromApi?: ProfileRow | null
    ): Promise<{ user: User | null; error: string | null }> => {
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: "magiclink",
        });
        if (error) return { user: null, error: error.message };
        if (profileFromApi) return { user: profileToUser(profileFromApi), error: null };
        const uid = data.user?.id;
        if (!uid) return { user: null, error: "Session invalide" };
        let prof: ProfileRow | null = null;
        let pe: { message: string } | null = null;
        const { data: profByUid, error: errUid } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", uid)
          .maybeSingle();
        if (errUid) pe = errUid;
        else prof = profByUid as ProfileRow | null;
        if (!prof && data.user?.email?.endsWith("@crm-led.local")) {
          const profileIdFromEmail = data.user.email.replace(/@crm-led\.local$/i, "").trim();
          if (profileIdFromEmail) {
            const { data: profByEmail, error: errEmail } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", profileIdFromEmail)
              .maybeSingle();
            if (!errEmail) prof = profByEmail as ProfileRow | null;
          }
        }
        if (pe) return { user: null, error: pe.message };
        if (!prof) return { user: null, error: "Profil introuvable" };
        return { user: profileToUser(prof), error: null };
      } catch (e) {
        return {
          user: null,
          error: e instanceof Error ? e.message : "Erreur vérification connexion",
        };
      }
    },
    []
  );

  return {
    loginAdmin,
    loginWithCode,
    getMagicLinkForCode,
    verifyTokenHashAndGetUser,
    registerAdmin,
    requestVerificationCode,
    verifyCodeAndRecover,
    logout,
    verifyMfaAndGetUser,
  };
}
